/**
 * Client for rpa-service `v1/delivery/run-submission-pipeline` (batch / filtered submission trigger).
 */
import { v4 as uuidv4 } from "uuid";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logDataToConsole, logError } from "../../utils/customLogger";
import {
  createRequestOptions,
  getAuthHeaders,
  handleApiResponse,
} from "../utils/apiHelpers";

/** Filter fields accepted by the delivery submission pipeline. */
export interface RunSubmissionPipelineFilters {
  filename?: string[];
  date_of_filing_start?: string;
  date_of_filing_end?: string;
  drug_name?: string[];
  form_name?: string[];
  [key: string]: unknown;
}

/** POST body for rpa-service `v1/delivery/run-submission-pipeline`. */
export interface RunSubmissionPipelineRequestBody {
  org_id: string;
  filters: RunSubmissionPipelineFilters;
  dry_run: boolean;
  idempotency_key: string;
}

/**
 * Invokes the pa-order-creation delivery submission pipeline (batch or scoped by filters).
 *
 * @param body - org_id, filters, dry_run, and idempotency_key
 * @param signal - Optional AbortSignal to cancel the request
 * @returns Parsed JSON response from the API
 */
export const runSubmissionPipeline = async (
  body: RunSubmissionPipelineRequestBody,
  signal?: AbortSignal,
): Promise<unknown> => {
  const headers = await getAuthHeaders();
  const requestOptions = createRequestOptions(
    body as unknown as Record<string, unknown>,
    headers,
    signal,
  );

  try {
    const response = await fetch(
      API_ENDPOINTS.RUN_SUBMISSION_PIPELINE,
      requestOptions,
    );
    const result = await handleApiResponse(response);
    logDataToConsole("runSubmissionPipeline", result);
    return result;
  } catch (error) {
    if ((error as Error).name === "AbortError") {
      logDataToConsole("runSubmissionPipeline", "Request aborted");
      throw error;
    }
    logError(error as Error, "Error in runSubmissionPipeline");
    throw error;
  }
};

/**
 * Builds a fresh idempotency key for a submission pipeline call, optionally namespaced.
 *
 * @param scope - Short label (e.g. tab id) to distinguish parallel triggers
 * @returns UUID-based idempotency string
 */
export const makeSubmissionPipelineIdempotencyKey = (scope: string): string =>
  `${uuidv4()}-${scope}`;
